import React, {useContext, useEffect, useState} from 'react';
import {Button, DatePicker, Descriptions, InputNumber, Popconfirm, Select, Space, Table, Tag} from 'antd';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {internalApi} from '../../../../common/axios';
import toast from 'react-hot-toast';
import IsLoading from '../../../../shared/components/IsLoading';
import dayjs from 'dayjs';

const DIFFICULTY_TEXT = {
    0: '보통',
    1: '쉬움',
    2: '어려움',
    3: 'NONE',
};

const STATE_TEXT = {
    0: {text: '시작 전', color: 'default'},
    1: {text: '휴식', color: 'blue'},
    2: {text: '진행중', color: 'processing'},
    3: {text: '클리어', color: 'green'},
    4: {text: '종료', color: 'default'},
};

const ROUND_RESULT_TEXT = {
    0: '없음',
    1: '성공',
    2: '실패',
};

const REWARD_TYPE_TEXT = {
    1: '라운드',
    2: '최종',
};

const PlayerBalloon = () => {
    const {playerData} = useContext(PlayerContext);
    const [pageData, setPageData] = useState(null);
    const [selectedEventSeq, setSelectedEventSeq] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);

    useEffect(() => {
        if (!playerData?.userKey) return;
        fetchBalloonData(null);
    }, [playerData?.userKey]);

    const fetchBalloonData = async (eventSeq) => {
        setLoading(true);
        try {
            const params = {userKey: playerData.userKey};
            if (eventSeq !== null) params.eventSeq = eventSeq;
            const {data} = await internalApi.get('/player/balloon', {params});
            setPageData(data);
            setForm(data.balloonInfo);
            if (data.eventSeq) setSelectedEventSeq(data.eventSeq);
        } catch {
            toast.error('드림 벌룬 데이터 로드 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleEventChange = (eventSeq) => {
        setSelectedEventSeq(eventSeq);
        fetchBalloonData(eventSeq);
    };

    const handleFormChange = (key, value) => {
        setForm((prev) => ({...prev, [key]: value}));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await internalApi.put('/player/balloon', {
                userKey: playerData.userKey,
                eventSeq: selectedEventSeq,
                difficulty: form.difficulty,
                currentRound: form.currentRound,
                state: form.state,
                lastRoundResult: form.lastRoundResult,
                roundStartAt: form.roundStartAt,
            });
            toast.success('저장되었습니다.');
            await fetchBalloonData(selectedEventSeq);
        } catch (e) {
            toast.error(e?.response?.data?.message || '저장 실패');
        } finally {
            setSaving(false);
        }
    };

    const handleResetReward = async (rewardType, round) => {
        try {
            await internalApi.post('/player/balloon/reward/reset', {
                userKey: playerData.userKey,
                eventSeq: selectedEventSeq,
                rewardType,
                round,
            });
            toast.success('보상 수령 기록이 초기화되었습니다.');
            await fetchBalloonData(selectedEventSeq);
        } catch (e) {
            toast.error(e?.response?.data?.message || '초기화 실패');
        }
    };

    const handleResetEvent = async () => {
        try {
            await internalApi.post('/player/balloon/reset', {
                userKey: playerData.userKey,
                eventSeq: selectedEventSeq,
            });
            toast.success('초기화되었습니다.');
            setSelectedEventSeq(null);
            await fetchBalloonData(null);
        } catch (e) {
            toast.error(e?.response?.data?.message || '초기화 실패');
        }
    };

    if (loading || !pageData) return <IsLoading/>;

    const {eventList = [], balloonInfo, rewardList = [], eventMaster} = pageData;

    const eventOptions = eventList.map(e => ({
        value: e.eventSeq,
        label: `이벤트 ${e.eventSeq} (${dayjs(e.updatedAt).format('YYYY-MM-DD HH:mm')})`,
    }));

    const rewardColumns = [
        {
            title: '종류', dataIndex: 'rewardType', key: 'rewardType', width: 100,
            render: v => <Tag color={v === 2 ? 'gold' : 'blue'}>{REWARD_TYPE_TEXT[v] || v}</Tag>,
        },
        {
            title: '라운드', dataIndex: 'round', key: 'round', width: 100,
            render: (v, row) => row.rewardType === 2 ? '-' : v,
        },
        {title: '수령 일시', dataIndex: 'claimedAt', key: 'claimedAt', width: 180},
        {
            title: '처리', key: 'action', width: 100,
            render: (_, row) => (
                <Popconfirm
                    title="보상 수령 기록을 초기화하시겠습니까? (재수령이 가능해집니다)"
                    onConfirm={() => handleResetReward(row.rewardType, row.round)}
                    okText="확인"
                    cancelText="취소"
                >
                    <Button size="small" danger>초기화</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div style={{maxWidth: 1100, margin: '0 auto', padding: 24}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20}}>
                <h2 style={{margin: 0}}>드림 벌룬 페스티벌</h2>
                {eventList.length > 0 && (
                    <Select
                        value={selectedEventSeq}
                        options={eventOptions}
                        onChange={handleEventChange}
                        style={{width: 260}}
                    />
                )}
            </div>

            {!balloonInfo ? (
                <div style={{padding: 24, textAlign: 'center', color: '#999'}}>
                    드림 벌룬 이벤트 진행 데이터가 없습니다.
                </div>
            ) : (
                <>
                    <div style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                        <h3 style={{marginBottom: 16}}>기본 정보</h3>
                        <Descriptions bordered size="small" column={2} style={{marginBottom: 16}}>
                            <Descriptions.Item label="이벤트 시퀀스">{balloonInfo.eventSeq}</Descriptions.Item>
                            <Descriptions.Item label="오픈 레벨">{eventMaster?.openLevel ?? '-'}</Descriptions.Item>
                            <Descriptions.Item label="상태">
                                <Tag color={STATE_TEXT[balloonInfo.state]?.color || 'default'}>{STATE_TEXT[balloonInfo.state]?.text || balloonInfo.state}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="마지막 라운드 결과">{ROUND_RESULT_TEXT[balloonInfo.lastRoundResult] || balloonInfo.lastRoundResult}</Descriptions.Item>
                            <Descriptions.Item label="난이도">{DIFFICULTY_TEXT[balloonInfo.difficulty] || balloonInfo.difficulty}</Descriptions.Item>
                            <Descriptions.Item label="현재 라운드">{balloonInfo.currentRound}</Descriptions.Item>
                            <Descriptions.Item label="라운드 시작 시각 (KST)">
                                {balloonInfo.roundStartAt ? dayjs.unix(Number(balloonInfo.roundStartAt)).format('YYYY-MM-DD HH:mm:ss') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="갱신 일시">{balloonInfo.updatedAt}</Descriptions.Item>
                        </Descriptions>

                        <h3 style={{marginBottom: 12}}>진행 정보 수정</h3>
                        <Space wrap size="middle" align="end">
                            <div>
                                <div style={{fontSize: 12, color: '#999', marginBottom: 4}}>난이도</div>
                                <Select
                                    value={form?.difficulty}
                                    onChange={(v) => handleFormChange('difficulty', v)}
                                    style={{width: 140}}
                                    options={Object.entries(DIFFICULTY_TEXT).map(([v, text]) => ({value: Number(v), label: text}))}
                                />
                            </div>
                            <div>
                                <div style={{fontSize: 12, color: '#999', marginBottom: 4}}>현재 라운드</div>
                                <InputNumber
                                    value={form?.currentRound}
                                    onChange={(v) => handleFormChange('currentRound', v)}
                                    min={0}
                                    style={{width: 140}}
                                />
                            </div>
                            <div>
                                <div style={{fontSize: 12, color: '#999', marginBottom: 4}}>상태</div>
                                <Select
                                    value={form?.state}
                                    onChange={(v) => handleFormChange('state', v)}
                                    style={{width: 140}}
                                    options={Object.entries(STATE_TEXT).map(([v, info]) => ({value: Number(v), label: info.text}))}
                                />
                            </div>
                            <div>
                                <div style={{fontSize: 12, color: '#999', marginBottom: 4}}>마지막 라운드 결과</div>
                                <Select
                                    value={form?.lastRoundResult}
                                    onChange={(v) => handleFormChange('lastRoundResult', v)}
                                    style={{width: 140}}
                                    options={Object.entries(ROUND_RESULT_TEXT).map(([v, text]) => ({value: Number(v), label: text}))}
                                />
                            </div>
                            <div>
                                <div style={{fontSize: 12, color: '#999', marginBottom: 4}}>라운드 시작 시각 (KST)</div>
                                <DatePicker
                                    showTime
                                    value={form?.roundStartAt ? dayjs.unix(form.roundStartAt) : null}
                                    onChange={(d) => handleFormChange('roundStartAt', d ? d.unix() : 0)}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    style={{width: 200}}
                                />
                            </div>
                            <Button type="primary" onClick={handleSave} loading={saving}>저장</Button>
                            <Popconfirm
                                title="정말로 초기화하시겠습니까? (진행 데이터와 보상 수령 기록이 모두 삭제됩니다)"
                                onConfirm={handleResetEvent}
                                okText="확인"
                                cancelText="취소"
                            >
                                <Button danger>이벤트 초기화</Button>
                            </Popconfirm>
                        </Space>
                    </div>

                    <div>
                        <h3 style={{marginBottom: 12}}>보상 수령 내역 ({rewardList.length}건)</h3>
                        <Table
                            dataSource={rewardList}
                            columns={rewardColumns}
                            rowKey={(r) => `${r.rewardType}-${r.round}`}
                            pagination={false}
                            size="small"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default PlayerBalloon;
